import { useCallback, useEffect, useMemo, useState } from "react";
import { posthog } from '../../../lib/posthog';
import type { DashboardThread } from "../types.js";
import type {
  DashboardMessageContact,
  DashboardMessageComposerSubmit,
  DashboardOpenOrCreateThreadActionResult,
} from "../messages.types.js";
import type { AdminApiResult } from "../../shared/api/types/adminApi";
import type {
  DashboardMessageContactRecordDto,
  DashboardMessagesSeedDto,
  DashboardThreadRecordDto,
  DashboardThreadOpenOrCreateResultDto,
  DashboardThreadMessageSendResultDto,
} from "../messages/types/messages.api";
import {
  mapDashboardMessageContactRecordToViewModel,
  mapDashboardMessageContactToRecord,
  mapDashboardMessagesSeedToViewModel,
  mapDashboardThreadRecordToViewModel,
  mapDashboardThreadToRecord,
} from "../messages/mappers/messagesMappers";
import {
  appendMessageToThread,
  cloneThreadsForState,
  createOutgoingThreadMessage,
  fetchDashboardMessagesSeed,
  filterDashboardContacts,
  filterDashboardThreads,
  getDashboardThreadById,
  getInitialDashboardThreadId,
  getSeededDashboardMessages,
  openOrCreateDashboardThreadForContactRequest,
  reconcileDashboardThreadMessageSend,
  sendDashboardThreadMessage,
  upsertDashboardThread,
} from "../messages/api/messagesApi";

export interface UseDashboardMessagesOptions {
  initialThreads?: DashboardThread[];
  initialSelectedThreadId?: string | null;
  loadSeedData?: () => Promise<AdminApiResult<DashboardMessagesSeedDto>>;
  sendThreadMessage?: (params: {
    threadId: string;
    messageId: string;
    submission: DashboardMessageComposerSubmit;
  }) => Promise<AdminApiResult<DashboardThreadMessageSendResultDto>>;
  openOrCreateThreadForContactRequest?: (params: {
    contact: DashboardMessageContactRecordDto;
    threads: DashboardThreadRecordDto[];
  }) => Promise<AdminApiResult<DashboardThreadOpenOrCreateResultDto>>;
}

function getInitialThreadsState(
  initialThreads?: DashboardThread[],
): DashboardThread[] {
  if (!initialThreads) {
    return getSeededDashboardMessages().threads;
  }

  return cloneThreadsForState(initialThreads);
}

function isValidThreadRecord(thread: DashboardThreadRecordDto): boolean {
  return thread.id.trim().length > 0;
}

export function useDashboardMessages({
  initialThreads,
  initialSelectedThreadId,
  loadSeedData = fetchDashboardMessagesSeed,
  sendThreadMessage = async ({ threadId, messageId, submission }) =>
    sendDashboardThreadMessage({
      ...submission,
      threadId,
      messageId,
    }),
  openOrCreateThreadForContactRequest = async ({ contact, threads }) =>
    openOrCreateDashboardThreadForContactRequest({
      contact,
      threads,
    }),
}: UseDashboardMessagesOptions = {}) {
  const [isLoading, setIsLoading] = useState(() => !initialThreads);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [seedLoadVersion, setSeedLoadVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [threads, setThreads] = useState<DashboardThread[]>(() =>
    initialThreads ? getInitialThreadsState(initialThreads) : [],
  );
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(() =>
    initialThreads
      ? getInitialDashboardThreadId(
          getInitialThreadsState(initialThreads),
          initialSelectedThreadId ?? null,
        )
      : null,
  );
  const [contacts, setContacts] = useState(() =>
    initialThreads
      ? getSeededDashboardMessages().contacts.map(
          mapDashboardMessageContactRecordToViewModel,
        )
      : [],
  );

  useEffect(() => {
    let isDisposed = false;

    async function hydrateMessagesState() {
      if (initialThreads) {
        const nextThreads = getInitialThreadsState(initialThreads);

        if (isDisposed) {
          return;
        }

        setThreads(nextThreads);
        setContacts(
          getSeededDashboardMessages().contacts.map(
            mapDashboardMessageContactRecordToViewModel,
          ),
        );
        setSelectedThreadId((currentSelectedThreadId) =>
          getInitialDashboardThreadId(
            nextThreads,
            initialSelectedThreadId ?? currentSelectedThreadId,
          ),
        );
        setErrorMessage(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const seedResult = await loadSeedData();
      if (isDisposed) {
        return;
      }

      if (!seedResult.ok) {
        setThreads([]);
        setContacts([]);
        setSelectedThreadId(null);
        setErrorMessage(seedResult.error.message);
        setIsLoading(false);
        return;
      }

      const nextSeed = mapDashboardMessagesSeedToViewModel(seedResult.data);
      const nextThreads = cloneThreadsForState(nextSeed.threads);
      setThreads(nextThreads);
      setContacts(nextSeed.contacts.map((contact) => ({ ...contact })));
      setSelectedThreadId((currentSelectedThreadId) =>
        getInitialDashboardThreadId(
          nextThreads,
          initialSelectedThreadId ?? currentSelectedThreadId,
        ),
      );
      setErrorMessage(null);
      setIsLoading(false);
    }

    void hydrateMessagesState();

    return () => {
      isDisposed = true;
    };
  }, [initialSelectedThreadId, initialThreads, loadSeedData, seedLoadVersion]);

  const filteredThreads = useMemo(
    () => filterDashboardThreads(threads, searchQuery),
    [searchQuery, threads],
  );

  const filteredContacts = useMemo(
    () => filterDashboardContacts(contacts, contactSearchQuery),
    [contactSearchQuery, contacts],
  );

  const activeThread = useMemo(
    () => getDashboardThreadById(threads, selectedThreadId),
    [selectedThreadId, threads],
  );

  const selectThread = useCallback((threadId: string) => {
    posthog.capture('thread_selected', { thread_id: threadId });
    setSelectedThreadId(threadId);
  }, []);

  const sendMessage = useCallback(
    (threadId: string, submission: DashboardMessageComposerSubmit) => {
      if (!submission.body.trim() && submission.attachments.length === 0) {
        return false;
      }

      posthog.capture('message_sent', { thread_id: threadId });

      const nextMessage = {
        ...createOutgoingThreadMessage(submission),
        status: "sending" as const,
      };
      setThreads((currentThreads) =>
        appendMessageToThread(currentThreads, threadId, nextMessage),
      );
      setSelectedThreadId(threadId);
      void sendThreadMessage({
        messageId: nextMessage.id,
        submission,
        threadId,
      })
        .then((result) => {
          setThreads((currentThreads) =>
            reconcileDashboardThreadMessageSend(currentThreads, {
              optimisticMessageId: nextMessage.id,
              sendResult: result,
              threadId,
            }),
          );
        })
        .catch(() => {
          // Defensive: transport adapters should return an ApiResult, but we
          // still reconcile to failed if a promise rejects unexpectedly.
          setThreads((currentThreads) =>
            reconcileDashboardThreadMessageSend(currentThreads, {
              optimisticMessageId: nextMessage.id,
              sendResult: null,
              threadId,
            }),
          );
        });
      return true;
    },
    [sendThreadMessage],
  );

  const upsertThread = useCallback((nextThread: DashboardThread) => {
    setThreads((currentThreads) => upsertDashboardThread(currentThreads, nextThread));
    setSelectedThreadId(nextThread.id);
  }, []);

  const retryLoad = useCallback(() => {
    setSeedLoadVersion((currentVersion) => currentVersion + 1);
  }, []);

  const openOrCreateThread = useCallback(
    async (
      contactId: string,
    ): Promise<DashboardOpenOrCreateThreadActionResult> => {
      try {
        const contact = contacts.find((entry) => entry.id === contactId);
        if (!contact) {
          return {
            ok: false,
            errorMessage: "Contact is unavailable right now.",
          };
        }

        const actionResult = await openOrCreateThreadForContactRequest({
          contact: mapDashboardMessageContactToRecord(contact),
          threads: cloneThreadsForState(threads).map(mapDashboardThreadToRecord),
        });
        if (!actionResult.ok) {
          return {
            ok: false,
            errorMessage: actionResult.error.message,
          };
        }

        if (!isValidThreadRecord(actionResult.data.thread)) {
          // Defensive runtime guard for malformed backend contracts.
          return {
            ok: false,
            errorMessage: "Unable to open this conversation right now.",
          };
        }

        const nextThread = mapDashboardThreadRecordToViewModel(
          actionResult.data.thread,
        );
        setThreads((currentThreads) =>
          upsertDashboardThread(currentThreads, nextThread),
        );
        setSelectedThreadId(nextThread.id);
        posthog.capture('thread_created', { thread_id: nextThread.id, contact_id: contactId, was_new: actionResult.data.created });
        return {
          ok: true,
          created: actionResult.data.created,
          thread: nextThread,
        };
      } catch {
        return {
          ok: false,
          errorMessage: "Unable to open this conversation right now.",
        };
      }
    },
    [contacts, openOrCreateThreadForContactRequest, threads],
  );

  return {
    activeThread,
    contactSearchQuery,
    contacts,
    errorMessage,
    filteredContacts,
    filteredThreads,
    isLoading,
    searchQuery,
    selectedThreadId,
    retryLoad,
    sendMessage,
    selectThread,
    setContactSearchQuery,
    setSearchQuery,
    openOrCreateThread,
    upsertThread,
  };
}
